import { useEffect, useState } from "react";
import { Clock, Gift, Users, Gamepad2, Crown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Pill } from "@/components/hub/Common";

export const fmtDate = (iso, lang) => new Date(iso).toLocaleString(lang, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

const left = (iso) => Math.max(0, new Date(iso).getTime() - Date.now());

export const Countdown = ({ to, testid = "challenge-countdown" }) => {
  const { t } = useI18n();
  const [ms, setMs] = useState(left(to));
  useEffect(() => { const id = setInterval(() => setMs(left(to)), 30000); return () => clearInterval(id); }, [to]);
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5), m = Math.floor((ms % 36e5) / 6e4);
  const cell = (v, u) => (
    <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2 min-w-[64px] text-center">
      <div className="font-display text-2xl font-bold text-white">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{u}</div>
    </div>
  );
  return <div className="flex gap-2" data-testid={testid}>{cell(d, t("ch.days"))}{cell(h, t("ch.hours"))}{cell(m, t("ch.minutes"))}</div>;
};

const TONE = { active: "rose", upcoming: "amber", ended: "slate" };

export const ChallengeHero = ({ c, children }) => {
  const { t, lang } = useI18n();
  return (
    <section className="cx-card cx-brackets rounded-3xl p-6 sm:p-10 relative overflow-hidden" data-testid="challenge-hero">
      <div className="absolute -top-28 -right-20 w-80 h-80 rounded-full bg-[#ff4d6d]/20 blur-3xl" />
      <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-[#2f7bff]/20 blur-3xl" />
      <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-end">
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2 items-center">
            <Pill tone={TONE[c.status]} testid="challenge-status">{t(`ch.${c.status}`)}</Pill>
            {c.game && <span className="flex items-center gap-1.5 text-xs text-slate-300 rounded-full bg-white/5 px-3 py-1" data-testid="challenge-game"><Gamepad2 className="w-3.5 h-3.5" />{c.game}</span>}
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-5xl mt-4 cx-chrome" data-testid="challenge-title">{c.title}</h2>
          {c.description && <p className="text-slate-300 mt-3 max-w-2xl whitespace-pre-line" data-testid="challenge-description">{c.description}</p>}
          <div className="flex flex-wrap gap-4 mt-5 text-sm">
            <span className="flex items-center gap-2 text-[#ffc94d]" data-testid="challenge-reward"><Gift className="w-4 h-4" />{t("ch.reward").replace("{n}", c.reward)}</span>
            <span className="flex items-center gap-2 text-slate-400" data-testid="challenge-entries-count"><Users className="w-4 h-4" />{(() => { const n = Array.isArray(c.entries) ? c.entries.length : c.entries; return t(n === 1 ? "ch.entries_one" : "ch.entries").replace("{n}", n); })()}</span>
          </div>
          {c.winner && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#ffc94d]/10 border border-[#ffc94d]/40 px-4 py-2 text-sm text-[#ffc94d]" data-testid="challenge-winner">
              <Crown className="w-4 h-4" />{t("ch.winner")} : {c.winner.handle ? `@${c.winner.handle}` : c.winner.creator} · {t((c.winner.score) === 1 ? "ch.score_one" : "ch.score").replace("{n}", c.winner.score)}
            </div>
          )}
          {children}
        </div>
        <div>
          {c.status === "active" && (<>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2"><Clock className="w-3.5 h-3.5" />{t("ch.ends_in")}</div>
            <Countdown to={c.ends_at} />
          </>)}
          {c.status === "upcoming" && <p className="text-sm text-slate-300" data-testid="challenge-starts">{t("ch.starts").replace("{date}", fmtDate(c.starts_at, lang))}</p>}
          {c.status === "ended" && <p className="text-sm text-slate-400" data-testid="challenge-ended-on">{t("ch.ended_on").replace("{date}", fmtDate(c.ends_at, lang))}</p>}
        </div>
      </div>
    </section>
  );
};
